import {
    Button,
    makeStyles,
    MessageBar,
    MessageBarActions,
    MessageBarBody,
    MessageBarGroup as FuiMessageBarGroup,
    type MessageBarIntent,
    MessageBarTitle,
} from '@fluentui/react-components';
import { useOnEvent } from '@fluentui/react-hooks';
import { DismissRegular } from '@fluentui/react-icons';
import { memo, useCallback, useEffect, useState } from 'react';
import type { PayloadError } from 'relay-runtime';

interface MessageProps {
    body: string;
    intent: MessageBarIntent;
    title?: string;
}

type GraphQLErrorExtensions = {
    classification?: string;
    exceptionChain?: Array<{ type: string; message: string }>;
};

type GraphQLError = Error & { extensions?: GraphQLErrorExtensions };

type EventDetail = MessageProps | MessageProps[];

type TCustomEvent = CustomEvent<EventDetail>;

const eventName = 'fismMsg';

export const MessageBarGroup = memo(() => {
    const [messages, setMessages] = useState<(MessageProps & { key: number })[]>([]);

    useOnEvent<HTMLBodyElement, TCustomEvent>(
        document,
        eventName,
        useCallback(({ detail }) => {
            const msgs = Array.isArray(detail) ? detail : [detail];
            if (msgs.length > 0) {
                const now = Date.now();

                setMessages((prevState) =>
                    prevState.concat(msgs.map((msg, index) => ({ ...msg, key: now + index })))
                );
            } else {
                setMessages((prevState) => (prevState.length > 0 ? [] : prevState));
            }
        }, [])
    );

    const styles = useStyles();

    const dismissMessage = useCallback((msg: MessageProps): void => {
        setMessages((prevState) => {
            const msgIndex = prevState.findIndex((entry) => entry === msg);
            if (msgIndex > -1) {
                const newState = Array.from(prevState);
                newState.splice(msgIndex, 1);
                return newState;
            }
            return prevState;
        });
    }, []);

    return messages.length < 1 ? null : (
        <FuiMessageBarGroup animate="exit-only" className={styles.root}>
            {messages.map((msg) => (
                <MessageBar className={styles.bar} intent={msg.intent} key={msg.key}>
                    <MessageBarBody className={styles.body}>
                        {msg.title && <MessageBarTitle>{msg.title}</MessageBarTitle>}
                        {msg.body}
                    </MessageBarBody>

                    <MessageBarActions
                        containerAction={<DismissAction dismiss={dismissMessage} msg={msg} />}
                    />
                </MessageBar>
            ))}
        </FuiMessageBarGroup>
    );
});
MessageBarGroup.displayName = 'MessageBarGroup';

const useStyles = makeStyles({
    bar: {
        whiteSpace: 'pre-wrap',
    },
    body: {
        overflowWrap: 'anywhere',
    },
    root: {
        display: 'grid',
        gap: '0.5em',
        gridAutoColumns: 'minmax(400px , auto)',
        overflow: 'auto',
    },
});

interface IDismissActionProps<T extends MessageProps = MessageProps> {
    dismiss: (msg: T) => void;
    msg: T;
}

const DismissAction = memo<IDismissActionProps>(({ dismiss, msg }) => {
    useEffect(() => {
        if (msg.intent === 'success') {
            const timeoutRef = setTimeout(() => {
                dismiss(msg);
            }, 3500);
            return () => {
                clearTimeout(timeoutRef);
            };
        }
    }, [dismiss, msg]);

    return (
        <Button
            onClick={() => {
                dismiss(msg);
            }}
            aria-label="Nachricht schließen"
            appearance="transparent"
            icon={<DismissRegular />}
        />
    );
});

export const triggerClearMessages = (): void => {
    document.dispatchEvent(new CustomEvent<[]>(eventName, { detail: [] }));
};

export const triggerEventToDisplayMessages = (detail: EventDetail): void => {
    document.dispatchEvent(new CustomEvent(eventName, { detail }));
};

export const triggerErrorMessage = (
    error: MessageProps['body'] | GraphQLError[] | PayloadError[],
    title?: MessageProps['title']
): void => {
    console.log(error);
    if (Array.isArray(error)) {
        if (error.length > 0) {
            document.dispatchEvent(
                new CustomEvent<MessageProps[]>(eventName, {
                    detail: error.map((err) => ({
                        body: err.message + getErrorDetails(err),
                        intent: 'error',
                        title,
                    })),
                })
            );
        }
        return;
    }

    document.dispatchEvent(
        new CustomEvent<MessageProps>(eventName, {
            detail: {
                body: error,
                intent: 'error',
                title,
            },
        })
    );
};

const isGraphQLError = (e: unknown): e is GraphQLError =>
    !!e && typeof e === 'object' && 'extensions' in e && !!(e as any).extensions;

const getErrorDetails = (error: GraphQLError | PayloadError): string => {
    if (
        isGraphQLError(error) &&
        error.extensions?.exceptionChain &&
        error.extensions.exceptionChain.length > 1
    ) {
        const chain = error.extensions.exceptionChain;
        return ' - (' + chain[chain.length - 1].message + ')';
    }
    return '';
};

export const triggerSuccessMessage = (
    message: MessageProps['body'],
    title?: MessageProps['title']
): void => {
    document.dispatchEvent(
        new CustomEvent<MessageProps>(eventName, {
            detail: {
                body: message,
                intent: 'success',
                title,
            },
        })
    );
};
